import { useState } from 'react';
import { Button, Dialog, Field, Icon, useErrorDialog, useToast } from '@dayon/design-system';
import { AUDIT_REASON_MIN_LENGTH } from '@dayon/contracts';
import { api, ApiRequestError } from '../../../lib/api.ts';

/**
 * Mark a player as an approved test account, or put them back — PAGCOR item 33(c).
 *
 * ── WHAT THIS CONTROL REALLY DOES, WHICH IS MORE THAN IT LOOKS ─────────────
 *
 * The form says the gaming transactions of an approved test account are not
 * accounted in Gross Gaming Revenue. So this does not label an account: it takes
 * its play, its deposits and its withdrawals OUT OF THE OPERATOR'S REPORTED
 * REVENUE — and not only from today.
 *
 * A test account is almost always identified after it has been used, so the
 * exclusion RETRACTS the days it already had: the next nightly rebuild removes
 * them, including days inside months that have already been reported. That is the
 * correct behaviour — a flag that only worked forwards would leave the month it
 * was created in overstated for ever — and it is emphatically not what anybody
 * expects from a checkbox. So the dialog says it before the press, in the place
 * somebody is actually looking.
 *
 * ── WHY IT ASKS FOR A SENTENCE, IN BOTH DIRECTIONS ────────────────────────
 *
 * "Approved" is the form's word. A tickbox records that somebody ticked a box,
 * not that anybody approved anything, and an account excluded from the operator's
 * own revenue figure is the first thing an inspection asks about.
 *
 * Unmarking asks too, because putting an account BACK into the revenue figures
 * changes a reported number just as surely as taking it out, and "it was marked
 * by mistake" is a sentence an inspection is entitled to read.
 *
 * The form state lives HERE rather than inside <Dialog>, which unmounts its
 * children when closed — a half-typed reason must survive a mis-clicked Cancel.
 */
export function TestAccountDialog({
  open,
  onClose,
  playerId,
  subject,
  /** What the account is TODAY, so the dialog knows which way it is going. */
  isTest,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  playerId: string;
  subject: string;
  isTest: boolean;
  onDone: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  /* Marking is the direction that removes revenue, so it carries the warning. */
  const marking = !isTest;

  const trimmed = reason.trim();
  /* Shown once they have started, never before: an error on an untouched field is
     a form telling somebody off for not having typed yet. */
  const error =
    reason.length > 0 && trimmed.length < AUDIT_REASON_MIN_LENGTH
      ? `At least ${AUDIT_REASON_MIN_LENGTH} characters. An inspection asks who decided this and why.`
      : undefined;
  const ready = trimmed.length >= AUDIT_REASON_MIN_LENGTH && trimmed.length <= 500;

  const submit = async () => {
    if (!ready || saving) return;
    setSaving(true);
    try {
      const answer = await api.post<{ reportingNote: string }>(
        `/api/players/${encodeURIComponent(playerId)}/test-account`,
        { isTest: marking, reason: trimmed },
      );
      /* THE SERVER'S OWN SENTENCE about what happens next, not one composed here:
         the rollup's timing is the server's fact, and a screen that guessed it
         would go on saying "tonight" after somebody changed the schedule. */
      toast(answer.reportingNote);
      setReason('');
      onClose();
      onDone();
    } catch (error_: unknown) {
      showError({
        title: marking ? 'Could not mark that account' : 'Could not put that account back',
        message:
          error_ instanceof ApiRequestError
            ? error_.message
            : 'Nothing was changed — the flag and its audit row land together or not at all.',
        ...(error_ instanceof ApiRequestError ? { code: error_.code } : {}),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        marking ? `Mark ${subject} as a test account` : `Put ${subject} back into the figures`
      }
      wide
      dismissible={false}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!ready || saving} onClick={() => void submit()}>
            {saving
              ? 'Saving…'
              : marking
                ? 'Mark as a test account'
                : 'Put back into the figures'}
          </Button>
        </>
      }
    >
      <div className="stack">
        {/* ── WHAT IT DOES, BEFORE THEY PRESS ────────────────────────────────
            The whole reason this is a dialog and not a toggle. The retraction is
            the part nobody expects, so it is stated first and in full. */}
        <div className={marking ? 'notice notice-danger' : 'notice notice-quiet'}>
          <Icon name={marking ? 'warning' : 'info'} />
          <div>
            {marking ? (
              <>
                <p>
                  <strong>This takes the account out of your revenue figures.</strong> Everything
                  it bets, deposits and withdraws stops counting towards Gross Gaming Revenue —
                  the figure a PAGCOR inspection reads.
                </p>
                <p className="text-subtle">
                  It also removes what the account has <strong>already</strong> done. The next
                  nightly rebuild retracts its past days, including days in months you have
                  already reported. That is deliberate: an account is usually recognised as a
                  test account after it has been used, and leaving its first month counted would
                  overstate it for ever.
                </p>
                <p className="text-subtle">
                  Nothing is deleted. What the account did is still on the account, and this can
                  be undone from the same button.
                </p>
              </>
            ) : (
              <>
                <p>
                  <strong>This puts the account back into your revenue figures.</strong> Its
                  past days return on the next nightly rebuild, so a month you have already
                  reported can change.
                </p>
                <p className="text-subtle">
                  Say why. Putting an account back changes a reported number just as surely as
                  taking it out.
                </p>
              </>
            )}
          </div>
        </div>

        <Field
          label="Why"
          hint="Goes on the audit trail and onto the account, where an inspector can read it."
          {...(error === undefined ? {} : { error })}
        >
          {({ id, describedBy, invalid }) => (
            <textarea
              id={id}
              className="input"
              rows={3}
              value={reason}
              maxLength={500}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                marking
                  ? 'e.g. QA rehearsal account for the SA integration, agreed with Finance'
                  : 'e.g. marked in error — this is a real player, confirmed with support'
              }
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
